/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { $insertDataTransferForRichText } from "@lexical/clipboard";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { AbList } from "abreact/input/AbList";
import { TEST_DATA_LOADED, TEST_UPDATE_COMMAND } from "abreact/input/markdown/AbMarkdownLexicalContent";
import { $createAbstractFieldNode, $isAbstractFieldNode, AbstractFieldNode } from "abreact/input/markdown/nodes/AbstractFieldNode";
import { TypeaheadMenuPlugin } from "abreact/input/markdown/plugins/TypeaheadMenuPlugin";
import { FIELD_TYPE, getAbstractFieldExtension, getInputFieldExtension, getNoAbstractFieldMatches, getParamFieldExtension } from "abreact/input/markdown/utils/MarkdownUtils";
import { useOnMount } from "abreact/utils/AbHooks";
import { EnumUtils } from "abweb/utils/EnumUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { $getNearestNodeFromDOMNode, $getSelection, $insertNodes, $isRangeSelection, $isTextNode, COMMAND_PRIORITY_LOW, KEY_ARROW_DOWN_COMMAND, KEY_ARROW_UP_COMMAND, KEY_DOWN_COMMAND } from "lexical";
import React, { useEffect } from "react";

// trigger key must match checkForFieldMatches
const FieldRegex = /[{$](.*)$/;
const fieldsCache = new Map();
const LOG = LogUtils.getLogger(__filename);
export let abLLMPromptDragDropData = /*#__PURE__*/function (abLLMPromptDragDropData) {
  abLLMPromptDragDropData["techName"] = "techName";
  abLLMPromptDragDropData["displayName"] = "displayName";
  abLLMPromptDragDropData["fieldType"] = "fieldType";
  abLLMPromptDragDropData["canonicalName"] = "canonicalName";
  abLLMPromptDragDropData["businessName"] = "businessName";
  abLLMPromptDragDropData["typeIcon"] = "typeIcon";
  abLLMPromptDragDropData["breEncoding"] = "breEncoding";
  return abLLMPromptDragDropData;
}({});
export function getRelevantExtension(type) {
  switch (type) {
    case FIELD_TYPE.INPUT:
      return getInputFieldExtension();
    case FIELD_TYPE.PARAM:
      return getParamFieldExtension();
    default:
      return getAbstractFieldExtension();
  }
}
function useExtensionMatching(nameSoFar) {
  const [results, setResults] = React.useState([]);
  React.useEffect(() => {
    const extension = getAbstractFieldExtension();
    if (extension === null) {
      setResults([]);
      return;
    }
    if (nameSoFar == null) {
      setResults([getNoAbstractFieldMatches()]);
      return;
    }
    const cachedResults = fieldsCache.get(nameSoFar);
    if (cachedResults === null) {
      return;
    } else if (cachedResults !== undefined) {
      setResults(cachedResults);
      return;
    }
    fieldsCache.set(nameSoFar, []);
    try {
      extension.getMatchingFields(nameSoFar).then(matchingFields => {
        fieldsCache.set(nameSoFar, matchingFields);
        setResults(matchingFields);
      });
    } catch (e) {
      // eslint-disable-next-line abinitio/no-instanceof
      LOG.error("Markdown Editor: getMatchingFields() failed:" + (e instanceof Error ? e.message : String(e)));
      setResults([]);
    }
  }, [nameSoFar]);
  return results;
}
export default function AbstractFieldPlugin(props) {
  const [editor] = useLexicalComposerContext();
  const [nameSoFar, setNameSoFar] = React.useState(null);
  const [lastKey, setLastKey] = React.useState(null);
  const [showPopup, setShowPopup] = React.useState(false);
  const [eventKey, setEventKey] = React.useState(null);
  const [testCache, setTestCache] = React.useState([]);
  const [saveSel, setSaveSel] = React.useState(null);
  const results = useExtensionMatching(nameSoFar);
  const [handledKey, setHandledKey] = React.useState(false);
  const nodeCache = new Map();
  const removeMutationListener = editor.registerMutationListener(AbstractFieldNode, mutatedNodes => {
    editor.update(() => {
      for (let [nodeKey, mutation] of mutatedNodes) {
        if (mutation === "destroyed") {
          if (props.viewMode === "default") {
            const targetNode = nodeCache.get(nodeKey);
            if (!targetNode) return;
            let nodesToUpdate = new Map();
            let noupdate = false;
            const nodeMap = editor.getEditorState()._nodeMap;
            for (const [key, node] of nodeMap) {
              if ($isAbstractFieldNode(node) && !noupdate) {
                if (node.getBusinessName() === targetNode.getBusinessName()) {
                  const match = nodesToUpdate.get(node.getBusinessName());
                  if (match && match.getCanonicalName() !== node.getCanonicalName()) {
                    noupdate = true;
                    break;
                  } else {
                    nodesToUpdate = nodesToUpdate.set(node.getBusinessName(), node);
                  }
                }
              }
            }
            !noupdate && nodesToUpdate.forEach(node => {
              node.setDisplayName(node.getBusinessName());
            });
            nodeCache.delete(nodeKey);
          }
        }
        if (mutation === "created") {
          const targetNode = editor.getEditorState()._nodeMap.get(nodeKey);
          if (!targetNode) return;
          if (!$isAbstractFieldNode(targetNode)) return;
          if ($isAbstractFieldNode(targetNode)) {
            if (targetNode.getTestView() !== props.viewMode) {
              targetNode.setTestView(props.viewMode);
            }
            let item = testCache.find(val => {
              if (val.type === FIELD_TYPE.INPUT) {
                return val.fieldDef.canonicalName === targetNode.getCanonicalName();
              } else {
                return val.fieldDef.techName === targetNode.getTechName();
              }
            });
            if (item && item.testName && item.testName !== targetNode.getTestName()) {
              targetNode.setTestName(item.testName);
            }
          }
          if (props.viewMode === "default") {
            nodeCache.set(nodeKey, targetNode);
            editor.getEditorState()._nodeMap.forEach(node => {
              if ($isAbstractFieldNode(node) && !node.is(targetNode)) {
                if (node.getBusinessName() === targetNode.getBusinessName() && node.getCanonicalName() !== targetNode.getCanonicalName()) {
                  if (node.getDisplayName() === node.getBusinessName()) {
                    node.setDisplayName(node.getCanonicalDisplayName());
                  }
                  targetNode.setDisplayName(targetNode.getCanonicalDisplayName());
                }
              }
            });
          }
        }
      }
    });
  });
  useEffect(() => {
    const editorElemContent = editor.getRootElement();
    const editorElemBox = editorElemContent?.parentElement?.parentElement;
    const handleDragStart = () => {
      editor.update(() => {
        const sel = $getSelection();
        if ($isRangeSelection(sel)) {
          setSaveSel(sel.clone());
        }
      });
    };
    const handleDrop = event => {
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer == null) return;
      const data = {
        techName: event.dataTransfer.getData(abLLMPromptDragDropData.techName),
        displayName: event.dataTransfer.getData(abLLMPromptDragDropData.displayName),
        fieldType: event.dataTransfer.getData(abLLMPromptDragDropData.fieldType),
        canonicalName: event.dataTransfer.getData(abLLMPromptDragDropData.canonicalName),
        //may be null for params
        businessName: event.dataTransfer.getData(abLLMPromptDragDropData.businessName),
        typeIcon: event.dataTransfer.getData(abLLMPromptDragDropData.typeIcon),
        breEncoding: event.dataTransfer.getData(abLLMPromptDragDropData.breEncoding)
      };

      // If the drop event doesn't have a LLMPrompt, try the HTML or Text flavors
      if (data.displayName === "") {
        editor.update(() => {
          const sel = $getSelection();
          if (sel) {
            // Dropping anything (e.g. an image) into a selection containing a code block has no effect
            const nodes = sel.getNodes();
            if (nodes.some(node => node.getTopLevelElement()?.getType() === "code")) return;
            $insertDataTransferForRichText(event.dataTransfer, sel, editor);
            saveSel?.removeText();
          }
        });
        return;
      }
      const fieldType = EnumUtils.enumValueByValue(FIELD_TYPE, data.fieldType);
      editor.update(() => {
        const node = $createAbstractFieldNode({
          displayName: data.displayName,
          type: fieldType,
          fieldDef: {
            techName: data.techName,
            canonicalName: data.canonicalName,
            businessName: data.businessName,
            typeIcon: data.typeIcon,
            breEncoding: data.breEncoding
          }
        });
        node && $insertNodes([node]);
      });
    };
    const handleDrag = event => {
      event.preventDefault();
      event.stopPropagation();
      if (!editorElemContent) {
        return false;
      }
      let isEndOfRow = true;
      const relativeY = event.clientY;
      const relativeX = event.clientX;
      let targetElem = null;
      const elems = Array.from(editorElemContent.getElementsByTagName("span"));
      let sel = editorElemContent.ownerDocument.getSelection();
      const caret = editorElemContent.ownerDocument.caretPositionFromPoint(relativeX, relativeY);
      if (sel && caret) {
        sel.setPosition(caret.offsetNode, caret.offset);
        targetElem = caret.offsetNode;
      } else {
        for (let element of elems) {
          const nodeRect = element.getBoundingClientRect();
          if (relativeY < nodeRect.bottom && relativeY > nodeRect.top) {
            if (relativeX < nodeRect.right && relativeX > nodeRect.left) {
              targetElem = element;
              isEndOfRow = false;
              break;
            } else {
              targetElem = element.parentElement;
              isEndOfRow = true;
            }
          }
        }
      }
      if (targetElem) {
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(targetElem);
          if (!$isTextNode(node)) {
            isEndOfRow ? node.selectEnd() : node.selectStart();
          }
        });
      } else {
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(elems[elems.length - 1]);
          node?.selectEnd();
        });
      }
    };
    if (editorElemBox) {
      editorElemBox.addEventListener('dragover', handleDrag, {
        capture: true
      });
      editorElemBox.addEventListener('drop', handleDrop, {
        capture: true
      });
      editorElemBox.addEventListener('dragstart', handleDragStart, {
        capture: true
      });
    }
    return () => {
      if (editorElemBox) {
        editorElemBox.removeEventListener('dragover', handleDrag, {
          capture: true
        });
        editorElemBox.removeEventListener('drop', handleDrop, {
          capture: true
        });
        editorElemBox.removeEventListener('dragstart', handleDragStart, {
          capture: true
        });
      }
    };
  }, [editor, saveSel]);
  useOnMount(() => {
    return editor.registerCommand(KEY_DOWN_COMMAND, event => {
      setLastKey(event.key);
      setEventKey(event);
      setHandledKey(false);
      return false;
    }, COMMAND_PRIORITY_LOW);
  });
  const options = React.useMemo(() => {
    return results;
  }, [results]);
  useOnMount(() => {
    return editor.registerCommand(TEST_UPDATE_COMMAND, () => {
      const extension = getAbstractFieldExtension();
      if (extension === null) {
        setTestCache([]);
        return false;
      }
      try {
        extension.getTestValues().then(matchingFields => {
          setTestCache(matchingFields);
          editor.dispatchCommand(TEST_DATA_LOADED, true);
        });
      } catch (e) {
        // eslint-disable-next-line abinitio/no-instanceof
        LOG.error("Markdown Editor: getMatchingFields() failed:" + (e instanceof Error ? e.message : String(e)));
        setTestCache([]);
      }
      return false;
    }, COMMAND_PRIORITY_LOW);
  });

  // handle the up and down arrow keys
  React.useEffect(() => {
    if (showPopup && !handledKey && (lastKey === "ArrowDown" || lastKey === "ArrowUp")) {
      setHandledKey(true);
      if (showPopup && eventKey) {
        eventKey.preventDefault();
        eventKey.stopPropagation();
      }
      lastKey === "ArrowDown" ? editor.dispatchCommand(KEY_ARROW_DOWN_COMMAND, new KeyboardEvent("keydown", {
        key: "ArrowDown"
      })) : editor.dispatchCommand(KEY_ARROW_UP_COMMAND, new KeyboardEvent("keydown", {
        key: "ArrowUp"
      }));
      setEventKey(null);
    }
  }, [lastKey, results, handledKey, showPopup, eventKey, editor]);
  React.useEffect(() => {
    editor.registerCommand(TEST_UPDATE_COMMAND, () => {
      fieldsCache.clear();
      return false;
    }, COMMAND_PRIORITY_LOW);
  });

  // handle the select person action
  const onSelectOption = React.useCallback((selectedOption, nodeToReplace, closeMenu) => {
    if (selectedOption === getNoAbstractFieldMatches()) return; // that special person is not a link

    const useSelection = selectedOption;
    editor.update(() => {
      const nodeToInsert = $createAbstractFieldNode(useSelection);
      if (nodeToReplace) {
        nodeToReplace.replace(nodeToInsert);
      } else {
        // insert at selection
        $insertNodes([nodeToInsert]);
      }
      nodeToInsert.selectEnd(); // move selection to after insertion
      closeMenu();
      setShowPopup(false);
    });
  }, [editor]);
  const checkForFieldMatch = text => {
    if (!handledKey && lastKey === "{" || lastKey === "$") {
      setShowPopup(true);
      setHandledKey(true);
    } else if (!showPopup) {
      return null;
    }
    const match = FieldRegex.exec(text);
    if (match !== null) {
      return {
        leadOffset: match.index,
        matchingString: match[1],
        replaceableString: match[0]
      };
    }
    return null;
  };
  const itemRenderer = field => {
    let info = field.fieldDef;
    if (info.techName === getNoAbstractFieldMatches().fieldDef.techName) {
      // the no-matches has a special renderer
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
        className: "choice-no-field-matches ab-italic"
      }, field.displayName));
    }
    return getRelevantExtension(field.type ?? FIELD_TYPE.NONE)?.renderChoice(field, nameSoFar || undefined) ??
    /*#__PURE__*/
    // The extension gets preference as renderer, but we have a backup.
    React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "choice-display-name"
    }, field.displayName), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
      className: "choice-id"
    }, info.techName));
  };
  const menuRenderFn = (anchorElementRef, {
    selectedIndex,
    selectOptionAndCleanUp,
    setHighlightedIndex
  }) => {
    return anchorElementRef.current && results.length ? /*#__PURE__*/React.createElement(AbPopup, {
      className: "ab-markdown-field-popup",
      origin: anchorElementRef.current ?? undefined,
      positioner: PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
      flushContent: true,
      overlay: false,
      onClose: () => setShowPopup(false),
      onBackgroundCaptureMouseDown: () => setShowPopup(false)
    }, /*#__PURE__*/React.createElement(AbList, {
      items: options,
      selectionMode: SelectionMode.SINGLE,
      itemRenderer: itemRenderer,
      onFocusChange: value => {
        value && selectOptionAndCleanUp(value);
      },
      scrollToIndex: selectedIndex ?? undefined,
      value: selectedIndex ? options[selectedIndex] : options[0],
      onChange: item => {
        const index = options.indexOf(item);
        setHighlightedIndex(index);
      }
    })) : null;
  };
  return /*#__PURE__*/React.createElement(TypeaheadMenuPlugin, {
    options: options,
    onQueryChange: setNameSoFar,
    onSelectOption: onSelectOption,
    triggerFn: checkForFieldMatch,
    menuRenderFn: menuRenderFn
  });
}
//# sourceMappingURL=AbstractFieldPlugin.js.map
